import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Home,
  Briefcase,
  Bell,
  Search,
  Sun,
  Moon,
  LogOut,
  ClipboardCheck,
  X,
  Menu,
} from "lucide-react";
import useTheme from "hooks/useTheme";
import { markPresenceOffline } from "hooks/usePresenceHeartbeat";
import {
  getUserProfile,
  setIsProfileLoading,
} from "reducer/profileSlice";
import { getAllNotifications } from "reducer/notificationSlice";
import { getActivePlanOfUser } from "reducer/paymentSlice";
import { removeTokens, serverFiles, formatPersonName } from "utils/utils";
import Modal from "components/Modal/Modal";
import BusinessProfileForm from "components/BusinessProfileForm/BusinessProfileForm";
import LegalProvider from "components/app/LegalModal";
import HeaderUserSearch from "components/app/HeaderUserSearch";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const NAV = [
  { to: "/feed", label: "Feed", Icon: Home },
  { to: "/projects", label: "Projects", Icon: Briefcase },
  {
    to: "/my-applications",
    label: "My applications",
    shortLabel: "Applications",
    Icon: ClipboardCheck,
    creativesOnly: true,
  },
  {
    to: "/notifications",
    label: "Notifications",
    shortLabel: "Alerts",
    Icon: Bell,
    showDot: true,
  },
];

const initialsOf = (profile) => {
  if (profile?.userType === "BUSINESS") {
    const name = profile?.companyResponseDto?.name || "Q";
    return name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  const name = formatPersonName(profile?.firstName, profile?.lastName);
  if (!name) return "Q";
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const displayNameOf = (profile) => {
  if (profile?.userType === "BUSINESS") {
    return profile?.companyResponseDto?.name || "Brand";
  }
  return (
    formatPersonName(profile?.firstName, profile?.lastName) || "Creative"
  );
};

export const AppShell = ({ children }) => {
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userProfile, isProfileLoading } = useSelector(
    (state) => state.userProfile
  );
  const logoSrc =
    theme === "light" ? "/qreativs-logo.png" : "/qreativs-logo-dark.png";

  const [unread, setUnread] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(false);

  const isBrand = userProfile?.userType === "BUSINESS";
  const companyLogo = userProfile?.companyResponseDto?.logo;
  const userProfileImage = userProfile?.profileUrl;
  const imageSrc =
    companyLogo || userProfileImage
      ? serverFiles(companyLogo || userProfileImage)
      : null;

  const mobileNav = NAV.filter((item) => !(item.creativesOnly && isBrand));

  useEffect(() => {
    const checkUserisValid = async () => {
      try {
        const { payload } = await dispatch(getUserProfile());
        if (
          payload?.userType === "BUSINESS" ||
          payload?.userType === "CREATOR"
        ) {
          dispatch(setIsProfileLoading(false));
        } else {
          removeTokens();
          navigate("/signin");
        }
      } catch (error) {
        console.log(error);
      }
    };
    checkUserisValid();
  }, [dispatch, navigate]);

  useEffect(() => {
    const myAlias =
      userProfile?.userAliasId ||
      userProfile?.companyResponseDto?.companyAliasId;
    const fetchAndCheckNotifications = async () => {
      try {
        const res = await dispatch(
          getAllNotifications({ page: 0, size: 20 })
        );
        const data = res?.payload || [];
        setUnread(
          data?.some?.(
            (n) =>
              n.isRead === false &&
              (!myAlias ||
                n?.actionUserDto?.actionUserAliasId !== myAlias)
          )
        );
      } catch (_) {
        setUnread(false);
      }
    };

    fetchAndCheckNotifications();
    const interval = setInterval(fetchAndCheckNotifications, 5000);
    return () => clearInterval(interval);
  }, [dispatch, pathname, userProfile]);

  useEffect(() => {
    if (userProfile?.userType === "BUSINESS") {
      dispatch(getActivePlanOfUser());
    }
  }, [userProfile?.userType, dispatch]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    await markPresenceOffline();
    localStorage.clear();
    navigate("/");
    window.location.reload();
  };

  const isActive = (to) =>
    pathname === to || (to !== "/feed" && pathname.startsWith(to));

  if (isProfileLoading && !userProfile) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400 font-sans-body">
        Loading…
      </div>
    );
  }

  return (
    <LegalProvider>
      <div className="landing-root min-h-screen bg-zinc-950 text-zinc-50 font-sans-body overflow-x-clip">
        <header className="app-header sticky top-0 z-50 border-b border-zinc-800/80 bg-zinc-950/75 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 h-14 sm:h-16 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
            {/* Left — brand + search (fills left column, stops before center nav) */}
            <div className="justify-self-stretch flex items-center gap-3 min-w-0 w-full overflow-visible pr-3 lg:pr-4">
              <Link to="/feed" className="shrink-0 inline-flex items-center">
                <img
                  src={logoSrc}
                  alt="Qreativs"
                  className="h-6 sm:h-7 w-auto"
                  draggable={false}
                />
              </Link>
              <HeaderUserSearch
                className="hidden md:block flex-1 min-w-0"
                placeholder="Search people…"
              />
            </div>

            {/* Center — primary nav (desktop / tablet) */}
            <nav className="justify-self-center relative z-10 hidden sm:flex items-center">
              <div className="app-header-nav flex items-center gap-0.5 rounded-full border border-zinc-800/70 bg-zinc-950/40 backdrop-blur-md px-1 py-1">
                {NAV.filter((item) => !(item.creativesOnly && isBrand)).map(
                  ({ to, label, shortLabel, Icon, showDot }) => {
                    const active = isActive(to);
                    const dot = showDot && unread;
                    const title = shortLabel || label;
                    return (
                      <Link
                        key={label}
                        to={to}
                        aria-label={label}
                        title={label}
                        className={`nav-icon relative h-9 w-9 lg:h-10 lg:w-auto lg:px-3.5 rounded-full flex items-center justify-center gap-2 transition-colors ${
                          active ? "is-active" : ""
                        }`}
                      >
                        <Icon size={18} />
                        <span className="hidden lg:inline text-[13px] font-medium tracking-wide">
                          {title}
                        </span>
                        {dot && (
                          <span className="absolute top-1.5 right-1.5 lg:static lg:ml-0.5 h-1.5 w-1.5 rounded-full bg-[#6C63F5] ring-2 ring-zinc-950 lg:ring-0 shrink-0" />
                        )}
                      </Link>
                    );
                  }
                )}
              </div>
            </nav>

            {/* Right — utilities */}
            <div className="justify-self-end flex items-center gap-1 sm:gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setMobileSearchOpen((v) => !v);
                }}
                aria-label="Search"
                className="nav-icon md:hidden h-10 w-10 rounded-full flex items-center justify-center transition-colors"
              >
                {mobileSearchOpen ? <X size={18} /> : <Search size={18} />}
              </button>

              <div className="hidden sm:flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={toggle}
                  aria-label="Toggle theme"
                  className="nav-icon h-10 w-10 rounded-full flex items-center justify-center transition-colors"
                >
                  {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                <Link
                  to="/user-profile"
                  aria-label="Profile"
                  className="h-9 w-9 rounded-full overflow-hidden border border-zinc-700 hover:border-[#6C63F5] transition-colors shrink-0"
                >
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt="You"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = NO_IMAGE;
                      }}
                    />
                  ) : (
                    <span className="avatar-fallback h-full w-full flex items-center justify-center text-xs font-semibold">
                      {initialsOf(userProfile)}
                    </span>
                  )}
                </Link>

                <button
                  type="button"
                  onClick={handleLogout}
                  aria-label="Log out"
                  className="nav-icon h-10 w-10 rounded-full flex items-center justify-center transition-colors"
                >
                  <LogOut size={18} />
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setMobileSearchOpen(false);
                  setMenuOpen((v) => !v);
                }}
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-expanded={menuOpen}
                className="nav-icon sm:hidden relative h-10 w-10 rounded-full flex items-center justify-center transition-colors"
              >
                <AnimatePresence mode="wait" initial={false}>
                  {menuOpen ? (
                    <motion.span
                      key="close"
                      initial={{ opacity: 0, rotate: -60, scale: 0.8 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: 60, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      className="flex"
                    >
                      <X size={20} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="menu"
                      initial={{ opacity: 0, rotate: 60, scale: 0.8 }}
                      animate={{ opacity: 1, rotate: 0, scale: 1 }}
                      exit={{ opacity: 0, rotate: -60, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                      className="flex"
                    >
                      <Menu size={20} />
                    </motion.span>
                  )}
                </AnimatePresence>
                {unread && !menuOpen && (
                  <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-[#6C63F5] ring-2 ring-zinc-950" />
                )}
              </button>
            </div>
          </div>

          {mobileSearchOpen && (
            <div className="md:hidden px-3 pb-3">
              <HeaderUserSearch
                autoFocus
                className="w-full"
                placeholder="Search people…"
                onNavigated={() => setMobileSearchOpen(false)}
              />
            </div>
          )}
        </header>

        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.button
                type="button"
                aria-label="Close menu"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="sm:hidden fixed inset-0 z-[60] app-mobile-menu-backdrop bg-black/55 backdrop-blur-[2px]"
                onClick={() => setMenuOpen(false)}
              />
              <motion.aside
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 380, damping: 34 }}
                className="app-mobile-menu sm:hidden fixed top-0 right-0 z-[70] h-dvh w-[min(20rem,88vw)] flex flex-col border-l backdrop-blur-xl"
              >
                <div className="app-mobile-menu-header flex items-center justify-between px-4 h-14 border-b">
                  <span className="text-xs uppercase tracking-[0.18em] font-semibold opacity-60">
                    Menu
                  </span>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    aria-label="Close menu"
                    className="nav-icon h-9 w-9 rounded-full flex items-center justify-center"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="px-4 pt-5 pb-4">
                  <Link
                    to="/user-profile"
                    onClick={() => setMenuOpen(false)}
                    className="app-mobile-menu-profile flex items-center gap-3 rounded-2xl border p-3.5 transition-colors"
                  >
                    <span className="h-11 w-11 rounded-full overflow-hidden border shrink-0 app-mobile-menu-avatar-ring">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = NO_IMAGE;
                          }}
                        />
                      ) : (
                        <span className="avatar-fallback h-full w-full flex items-center justify-center text-sm font-semibold">
                          {initialsOf(userProfile)}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="app-mobile-menu-name block text-sm font-semibold truncate">
                        {displayNameOf(userProfile)}
                      </span>
                      <span className="block text-xs mt-0.5 opacity-55">
                        View profile
                      </span>
                    </span>
                  </Link>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
                  {mobileNav.map(({ to, label, Icon, showDot }) => {
                    const active = isActive(to);
                    const dot = showDot && unread;
                    return (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setMenuOpen(false)}
                        className={`app-mobile-menu-link relative flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors ${
                          active ? "is-active" : ""
                        }`}
                      >
                        {active && (
                          <span className="app-mobile-menu-active-bar absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full" />
                        )}
                        <span
                          className={`app-mobile-menu-icon relative flex h-9 w-9 items-center justify-center rounded-full border ${
                            active ? "is-active" : ""
                          }`}
                        >
                          <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
                          {dot && (
                            <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[#6C63F5] ring-2 app-mobile-menu-dot-ring" />
                          )}
                        </span>
                        {label}
                      </Link>
                    );
                  })}
                </nav>

                <div className="app-mobile-menu-footer mt-auto border-t px-3 py-3 space-y-1 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                  <button
                    type="button"
                    onClick={toggle}
                    className="app-mobile-menu-link w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors"
                  >
                    <span className="app-mobile-menu-icon flex h-9 w-9 items-center justify-center rounded-full border">
                      {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
                    </span>
                    {theme === "dark" ? "Light mode" : "Dark mode"}
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="app-mobile-menu-logout w-full flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-colors"
                  >
                    <span className="app-mobile-menu-logout-icon flex h-9 w-9 items-center justify-center rounded-full border">
                      <LogOut size={17} />
                    </span>
                    Log out
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <main className="min-w-0">{children}</main>

        {businessProfile && (
          <Modal
            width="40rem"
            background="var(--ch-bg-modal, #18181b)"
            modalOpen={businessProfile}
            onClose={() => setBusinessProfile(false)}
          >
            <BusinessProfileForm setBusinessProfile={setBusinessProfile} />
          </Modal>
        )}
      </div>
    </LegalProvider>
  );
};

export default AppShell;
